import React, { useEffect, useRef } from 'react';
import { API_URL } from '../lib/api-client';
import { toast } from 'sonner';

interface GoogleSignInButtonProps {
  onSuccess: (data: any) => void;
  text?: string;
}

export function GoogleSignInButton({ onSuccess, text = 'Sign in with Google' }: GoogleSignInButtonProps) {
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const GOOGLE_CLIENT_ID = '952255380263-fv8mdmcvaokgcp1ccm5ac18n0d4ueimk.apps.googleusercontent.com';

  useEffect(() => {
    const handleCredentialResponse = async (response: any) => {
      try {
        const targetApiUrl = (API_URL && API_URL.startsWith('http')) ? API_URL : 'http://localhost:5001/api';
        const res = await fetch(`${targetApiUrl}/auth/google-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: response.credential })
        });
        const data = await res.json();
        if (data.success && data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          toast.success(data.message || 'Logged in with Google!');

          if (!data.user?.profileCompleted) {
            window.location.href = '/complete-profile';
          } else if (data.user?.adminVerification?.status === 'pending') {
            window.location.href = '/admin-pending';
          } else {
            window.location.href = '/app';
          }
          
          if (onSuccess) {
            onSuccess(data);
          }
        } else {
          toast.error(data.message || 'Google sign-in failed');
        }
      } catch (err: any) {
        toast.error('Google Sign-In Error: ' + (err.message || 'Network error'));
      }
    };

    const initializeGoogle = () => {
      if ((window as any).google?.accounts?.id) {
        try {
          (window as any).google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            context: 'signin'
          });

          if (googleBtnRef.current) {
            (window as any).google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline',
              size: 'large',
              width: 320,
              text: 'continue_with',
              shape: 'pill'
            });
          }

          // Trigger One-Tap Auto Popup
          (window as any).google.accounts.id.prompt();
        } catch (e) {
          console.warn('Google Identity initialization error:', e);
        }
      }
    };

    if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else {
      initializeGoogle();
    }
  }, [onSuccess]);

  return (
    <div className="w-full flex flex-col items-center my-4">
      <div className="relative w-full flex items-center justify-center my-3">
        <div className="border-t border-gray-200 w-full" />
        <span className="bg-white px-3 text-xs text-gray-500 uppercase tracking-wider absolute">Or continue with</span>
      </div>
      <div ref={googleBtnRef} className="w-full flex justify-center min-h-[44px] my-2" />
    </div>
  );
}
